import { useEffect, useMemo, useState } from 'react';
import { TrashIcon } from '@bizfirst/common-react';
import type { AcceptedCredentialType, CredentialDTO, CredentialFormSchema, CredentialTypeDTO } from '../../types';
import { useCredentialAdminApiClient, useCredentialFormRenderer, useCredentialOAuthConnect } from '../../context/AiAgentAdminApiContext';
import { Modal } from '../Shared/Modal';
import { ConfirmDialog } from '../Shared/ConfirmDialog';

export interface CredentialEditorProps {
  /** Omit to create; pass an existing CredentialID to edit. */
  credentialId?: number;
  /** Pre-selected type code for create mode when the caller already knows it (e.g. type-aware CredentialPicker). */
  defaultCredentialTypeCode?: string;
  /** Types selectable in the type-picker step when defaultCredentialTypeCode/credentialId are both absent. */
  allowedCredentialTypes: CredentialTypeDTO[];
  /** The CredentialPicker's matching AcceptedCredentialType entry for the resolved type, when known
   *  (carries editFormID/viewFormID) — threaded down to a custom CredentialFormRenderer so it can
   *  resolve the right form. Undefined for a search-all/type-agnostic picker. */
  acceptedCredentialType?: AcceptedCredentialType;
  onClose: () => void;
  onSaved: (credential: CredentialDTO) => void;
}

// Same hardcoded defaults flow-studio's CredentialDialog.tsx uses (ExecutionScopeID=1, IsShared=true)
// — not user-configurable in this pass either; ported as-is rather than silently invented anew.
const DEFAULT_EXECUTION_SCOPE_ID = 1;

const EMPTY_SCHEMA: CredentialFormSchema = { controls: [] };

// Error code the backend must return from credentials/get-by-id/decrypted when the saved secret
// exists but cannot be decrypted on this server. It is the only decrypt failure that opens replace mode.
const UNREADABLE_SECRET_CODE = 'CREDENTIAL_UNREADABLE';

/** A field counts as filled when it has a value. Blank text is not filled. Checkboxes are false, not blank. */
function isFieldFilled(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  return !(typeof value === 'string' && value.trim() === '');
}

/**
 * Create/edit form for a Credential — adapted from flow-studio-designer's CredentialDialog.tsx.
 * The dynamic per-type field list and the OAuth2 "Connect" control are both pluggable extension
 * points (`useCredentialFormRenderer()`/`useCredentialOAuthConnect()`, resolved from the nearest
 * `<AiAgentAdminApiProvider>`): by default they render plain HTML controls straight off
 * `CredentialFormSchema.controls` (see `defaultCredentialFormRenderer.tsx`) with no OAuth popup
 * (a plain-text note instead) — octopus-admin/did-admin/octopus-a2a all use these defaults
 * unchanged. Flow Studio supplies its own Atlas-Forms-backed implementations instead (see
 * `AtlasCredentialFormAdapter.tsx`) to restore its original dynamic form + OAuth popup behavior.
 */
export function CredentialEditor({ credentialId, defaultCredentialTypeCode, allowedCredentialTypes, acceptedCredentialType, onClose, onSaved }: CredentialEditorProps) {
  const api = useCredentialAdminApiClient();
  const FormRenderer = useCredentialFormRenderer();
  const OAuthConnect = useCredentialOAuthConnect();
  const [step, setStep] = useState<'selectType' | 'form'>(credentialId || defaultCredentialTypeCode ? 'form' : 'selectType');
  const [credentialTypeCode, setCredentialTypeCode] = useState<string | undefined>(defaultCredentialTypeCode);
  const [credentialType, setCredentialType] = useState<CredentialTypeDTO | null>(null);
  const [schema, setSchema] = useState<CredentialFormSchema>(EMPTY_SCHEMA);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(!!credentialId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // The backend reports UNREADABLE_SECRET_CODE only when the saved secret exists but this server
  // cannot decrypt it (for example, it was encrypted with a different tenant key). The form opens
  // empty in replace mode: the admin enters the secret again, and saving replaces the stored one.
  // The name, description, and type are still loaded.
  const [secretUnreadable, setSecretUnreadable] = useState(false);
  // Any other decrypt failure (network, timeout, 5xx, parse error, unknown code) is treated as
  // possibly transient. Saving stays blocked so blank fields cannot overwrite a valid stored secret.
  const [decryptBlocked, setDecryptBlocked] = useState(false);
  // The credential record itself could not be loaded, so there is no type or name to save with.
  const [loadBlocked, setLoadBlocked] = useState(false);

  const resolvedType = useMemo(
    () => credentialType ?? allowedCredentialTypes.find((t) => t.Code === credentialTypeCode) ?? null,
    [credentialType, credentialTypeCode, allowedCredentialTypes]
  );

  const loadSchema = async (typeId: number) => {
    const result = await api.getFormSchema(typeId);
    setSchema(result.data?.schema ?? EMPTY_SCHEMA);
  };

  useEffect(() => {
    if (credentialId) {
      setLoading(true);
      Promise.all([api.getById(credentialId), api.getByIdDecrypted(credentialId)]).then(async ([byId, decrypted]) => {
        if (byId.success && byId.data) {
          setName(byId.data.Name);
          setDescription(byId.data.Description ?? '');
          const type = allowedCredentialTypes.find((t) => t.CredentialTypeID === byId.data!.CredentialTypeID) ?? null;
          setCredentialType(type);
          setCredentialTypeCode(type?.Code);
          await loadSchema(byId.data.CredentialTypeID);
        } else {
          setLoadBlocked(true);
          setError('This credential could not be loaded. Close this dialog and try again.');
        }
        if (decrypted.success && decrypted.data) {
          // decrypted.data is already the parsed field bag (credentialAdminApiClient.getByIdDecrypted
          // JSON.parses the backend's raw decrypted string) — do NOT index a further `.data` off it.
          setFieldValues(decrypted.data);
        } else if (decrypted.error?.code === UNREADABLE_SECRET_CODE) {
          // Only the backend's explicit "cannot decrypt" signal opens replace mode. Start with empty
          // fields so nothing unreadable is shown or saved back.
          setSecretUnreadable(true);
          setFieldValues({});
        } else {
          // Unknown or transient failure. Do not fall back to {}: an empty field bag would be saved
          // over the real encrypted data (2026-09-14 incident). Block saving and ask the admin to retry.
          setDecryptBlocked(true);
          setError('Could not load this credential’s saved values. Editing is disabled to avoid overwriting them — close this dialog and try again.');
        }
        setLoading(false);
      });
      return;
    }
    if (defaultCredentialTypeCode) {
      const type = allowedCredentialTypes.find((t) => t.Code === defaultCredentialTypeCode) ?? null;
      setCredentialType(type);
      if (type) loadSchema(type.CredentialTypeID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialId]);

  const handlePickType = async (type: CredentialTypeDTO) => {
    setCredentialType(type);
    setCredentialTypeCode(type.Code);
    await loadSchema(type.CredentialTypeID);
    setStep('form');
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Credential name is required.');
      return;
    }
    if (!resolvedType) {
      setError('A credential type must be selected.');
      return;
    }
    if (loadBlocked) {
      setError('This credential could not be loaded, so it cannot be saved. Close this dialog and try again.');
      return;
    }
    if (decryptBlocked) {
      setError('Could not load this credential’s saved values earlier — refusing to save to avoid overwriting them. Close this dialog and try again.');
      return;
    }
    // Replace mode writes the whole secret, so every field must be filled. A blank field would be
    // saved as empty, and the old value cannot be recovered to put back.
    if (secretUnreadable && !schema.controls.every((control) => isFieldFilled(fieldValues[control.id]))) {
      setError('Enter every field to replace the saved secret. Close this dialog to keep it unchanged.');
      return;
    }
    const missing = schema.controls.filter((control) => control.required && !isFieldFilled(fieldValues[control.id]));
    if (missing.length > 0) {
      setError(`Required: ${missing.map((control) => control.label).join(', ')}.`);
      return;
    }
    setSaving(true);
    setError(null);
    const request = {
      Name: name,
      Description: description,
      CredentialTypeID: resolvedType.CredentialTypeID,
      EncryptedData: JSON.stringify(fieldValues),
      ExecutionScopeID: DEFAULT_EXECUTION_SCOPE_ID,
      IsShared: true,
    };
    const result = credentialId ? await api.update(credentialId, request) : await api.create(request);
    setSaving(false);
    if (result.success && result.data) {
      onSaved(result.data);
    } else {
      setError(result.error?.message ?? 'Failed to save credential.');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!credentialId) return;
    setDeleting(true);
    const result = await api.deleteById(credentialId);
    setDeleting(false);
    if (result.success) {
      onClose();
    } else {
      setError(result.error?.message ?? 'Failed to delete credential.');
      setConfirmDelete(false);
    }
  };

  if (step === 'selectType') {
    return (
      <Modal isOpen title="Create Credential — Select Type" onClose={onClose} width={480}>
        <div className="aaa-entity-grid">
          {allowedCredentialTypes.map((type) => (
            <button key={type.CredentialTypeID} type="button" className="aaa-entity-card aaa-entity-card-button" onClick={() => handlePickType(type)}>
              <strong>{type.Name}</strong>
              {type.Description && <p>{type.Description}</p>}
            </button>
          ))}
          {allowedCredentialTypes.length === 0 && <div className="aaa-entity-dropdown-empty">No credential types available.</div>}
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen title={credentialId ? `Edit Credential — ${name || '…'}` : `Create Credential — ${resolvedType?.Name ?? ''}`} onClose={onClose} width={640}>
      {loading ? (
        <div className="aaa-editor-loading">Loading…</div>
      ) : (
        <>
          {secretUnreadable && (
            <div className="aaa-editor-notice">
              <strong>The saved secret can’t be read on this server</strong>, so it can’t be shown.{' '}
              {schema.controls.length > 0
                ? 'Enter the secret again to replace it. The name and description are kept.'
                : 'This credential type has no form fields defined yet, so the secret can’t be re-entered here. Define the type’s form schema first, then replace the secret.'}
            </div>
          )}
          {error && <div className="aaa-editor-error">{error}</div>}
          <div className="aaa-form-grid">
            <div className="aaa-form-group aaa-form-group-full">
              <label>Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="aaa-form-group aaa-form-group-full">
              <label>Description</label>
              <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>

            {resolvedType?.IsOAuth && (
              OAuthConnect ? (
                <OAuthConnect
                  credentialType={resolvedType}
                  formValues={fieldValues}
                  onExchanged={(fieldUpdates) => setFieldValues((f) => ({ ...f, ...fieldUpdates }))}
                />
              ) : (
                <div className="aaa-form-group-full aaa-editor-note">
                  This is an OAuth credential type. The interactive "Connect" popup flow is not yet built
                  in this admin UI — paste the token/secret values below directly (see Flow Studio's
                  credential dialog for the guided OAuth flow in the meantime).
                </div>
              )
            )}

            {resolvedType && (
              <FormRenderer
                schema={schema}
                credentialType={resolvedType}
                acceptedType={acceptedCredentialType}
                mode={credentialId ? 'edit' : 'create'}
                values={fieldValues}
                onFieldChange={(fieldId, value) => setFieldValues((f) => ({ ...f, [fieldId]: value }))}
              />
            )}
          </div>

          <div className="aaa-editor-footer">
            <div>
              {credentialId && (
                <button type="button" className="aaa-btn aaa-btn-danger" onClick={() => setConfirmDelete(true)} disabled={saving}>
                  <TrashIcon size={13} /> Delete Credential
                </button>
              )}
            </div>
            <div className="aaa-editor-footer-actions">
              <button type="button" className="aaa-btn aaa-btn-default" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving || loadBlocked || decryptBlocked || (secretUnreadable && schema.controls.length === 0)}>
                {saving ? 'Saving…' : credentialId ? 'Save Changes' : 'Create Credential'}
              </button>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={confirmDelete}
        title="Delete Credential"
        message={`Delete "${name}"? This soft-deletes the credential.`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmDelete(false)}
      />
    </Modal>
  );
}
